import React from "react";
import HelloSign from "hellosign-embedded";
import dayjs from "dayjs";
import styled from 'styled-components';
import Modal, { ModalProvider, BaseModalBackground } from 'styled-react-modal';
import { CloseOutlined } from '@ant-design/icons';

import _ from "lodash";
import { Flex, Text, Box, ActionNote } from "../components";
import * as S from "../styles";
import FormConsumer from "../collect-info/FormConsumers";
import { Button, SecondaryButton } from "../styles";
import Axios from "../axios";
import { toFormData } from "../../../assets/js/utils";
import {
  skipDomainVerificationHelloSign,
  helloSignClientId,
} from "../../../config";
import {
  stateOptions,
  STEPS,
  step1Questions,

  // step 2
  residentStatusQuestion,
  lawfulNumberQuestion,
  alienCodeQuestion,
  alienNumberQuestion,
  alienCountryQuestion,
  admissionNumberQuestion,
  foreignPassportQuestion,
} from "./data";
import PikadayReact from "../PikadayReact";
import { getSecurePDFLink } from "./EmployerFillForm";
import { PageTitle } from '../biz-components/page-title';
import NoticeStep from "./NoticeStep";
import AttestationCheckbox from "./AttestationCheckbox";
import { pendingNotices, isNoticeGateError } from "./noticeApi";


const isSameOrBefore = require('dayjs/plugin/isSameOrBefore');

dayjs.extend(isSameOrBefore);

const PendingSSNModal = Modal.styled`
  width: 100%;
  max-width: 400px;
  margin: 16px;
  padding: 20px 16px 16px;
  background-color: white;
  border-radius: 12px;
`;

const PendingSSNModalBackground = styled(BaseModalBackground)`
  z-index: 1000;
`;

class EmployeeFillForm extends React.Component {
  constructor(props) {
    super(props);
    const {
      action: {
        i9_form_submission: { data = {} },
      },
      dataFromEi = {},
    } = props;
    const mergedData = isOnboardingV2 === 'true' ? { ...data, ...dataFromEi } : data;
    const step1QuestionsWithAnswers = this.injectAnswersToQuestions(
      { questions: step1Questions, data: mergedData },
      "step1"
    );

    // Check if pending SSN feature is enabled
    const isPendingSSNFeatureEnabled = window.isPendingSSNEnabled === 'true';

    // Apply conditional validation for SSN based on existing pending_ssn value
    const finalStep1Questions = step1QuestionsWithAnswers.map((q) => {
      if (isPendingSSNFeatureEnabled && q.id === 'social_security_number' && (mergedData.pending_ssn === true || mergedData.pending_ssn === "true")) {
        return {
          ...q,
          is_required: false, // SSN is not required when pending_ssn is checked
          // disabled is left false so the field title is not greyed out;
          // hideInput removes the input itself while pending
          hideInput: true,
          answer: {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              social_security_number: '', // Clear SSN when pending_ssn is checked
            },
          },
        };
      }
      return q;
    });

    // For the question with the id 'alien_code', we need to calculate the value
    // based on other saved values, because it's not a necessary data fro BE but
    // only a condition radio in FE, so BE didn't save this data directly
    if (data.alien_registration_or_uscis_number) {
      data.alien_code = "1";
    } else if (data.admission_number) {
      data.alien_code = "2";
    } else if (data.passport_number) {
      data.alien_code = "3";
    }

    let currentStep = STEPS.step1;
    if (props.viewResultAsManager) {
      currentStep = STEPS.step3;
    }

    this.state = {
      data,
      currentStep,
      saveDataError: false,
      signError: false,

      // pre-Section 1 notices (keys acknowledged in this session; the payload
      // still lists them as pending until the next load)
      acknowledgedNoticeKeys: [],
      awaitingNoticeRefresh: false,

      // step 1 data (Enter personal information)
      step1Questions: finalStep1Questions,
      pendingSSN: isPendingSSNFeatureEnabled ? (mergedData.pending_ssn === true || mergedData.pending_ssn === "true") : false,
      showPendingSSNConfirm: false,

      // step 2 data (resident status)
      step2Questions: this.getStep2Questions({ data }),
      noExpirationDate: false,
      expirationDate: data.alien_authorized_to_work_expiration_date || "",
      expirationDateError: undefined,

      // step 4 data (signing)
      signLink: undefined,

      cfaComplianceCheck: false,
      cfaComplianceCheckError: false,
      section1InitialTime: '',
    };

    this.step1Form = React.createRef();
    this.step2Form = React.createRef();

    this.isOnboardingV2 = isOnboardingV2 === 'true';
    this.isPendingSSNEnabled = isPendingSSNFeatureEnabled;
  }

  componentDidMount() {
    this.setState({
      section1InitialTime: new Date().toISOString(),
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.action !== this.props.action && this.state.awaitingNoticeRefresh) {
      this.clearNoticeRefreshTimer();
      this.setState({ awaitingNoticeRefresh: false });
    }
  }

  isPreviewMode() {
    if (this.props.previewMode) return true;
    return typeof previewMode !== 'undefined' && Boolean(previewMode);
  }

  isWorkerMode() {
    return !this.props.viewResultAsManager && !this.isPreviewMode();
  }

  currentPendingNotices() {
    const { acknowledgedNoticeKeys } = this.state;
    return pendingNotices(this.props.action).filter(
      (notice) => !acknowledgedNoticeKeys.includes(notice.key),
    );
  }

  showNoticeStep() {
    if (!this.isWorkerMode()) return false;
    return this.state.awaitingNoticeRefresh || this.currentPendingNotices().length > 0;
  }

  workerStepLock() {
    if (this.isPreviewMode()) {
      return { disabled: true, disabledText: "You cannot complete this step in Preview Mode." };
    }
    if (this.props.companyStaff && this.props.companyStaff.id) {
      return { disabled: true, disabledText: "This step should be completed by Employee." };
    }
    return { disabled: false, disabledText: undefined };
  }

  clearNoticeRefreshTimer() {
    if (this.noticeRefreshTimer) {
      clearTimeout(this.noticeRefreshTimer);
      this.noticeRefreshTimer = undefined;
    }
  }

  // The backend answered 60045: an applicable notice has not been acknowledged.
  // Pull the payload again so the pending item (and its copy) arrives, show the
  // notice step, and keep the current step and its form state for afterwards.
  routeToNoticeStep({ fromSigning = false } = {}) {
    const { refreshProgressDataFn } = this.props;
    if (fromSigning && this.hellosignClient) this.hellosignClient.close();

    this.setState({
      isSubmitting: false,
      saveDataError: refreshProgressDataFn ? false : "Please reload this page to continue.",
      awaitingNoticeRefresh: Boolean(refreshProgressDataFn),
      ...(fromSigning && { currentStep: STEPS.step3, signLink: undefined, signError: false }),
    });

    if (!refreshProgressDataFn) return;
    refreshProgressDataFn();
    this.clearNoticeRefreshTimer();
    this.noticeRefreshTimer = setTimeout(() => {
      this.noticeRefreshTimer = undefined;
      this.setState({ awaitingNoticeRefresh: false });
    }, 15000);
  }

  handleNoticeAcknowledged(item) {
    this.setState((prevState) => ({
      acknowledgedNoticeKeys: [...prevState.acknowledgedNoticeKeys, item.key],
    }));
  }

  shouldPrepopulate(config) {
    if (config && "skip_prepopulate" in config) {
      return (
        config.skip_prepopulate === false || config.skip_prepopulate === "false"
      );
    }
    return true;
  }

  renderFixedModuleHeader(text, link) {
    return (
      <a
        target="_blank"
        rel="noopener noreferrer"
        style={{ lineHeight: "20px", textDecoration: "none", color: "#3F51B5" }}
        href={link}
      >
        <Box width="fit-content" marginBottom="8px">
          <Flex alignItems="center" backgroundColor="#F4F4F5" padding="6px 8px">
            <Text fontSize={12} color="#787883" fontWeight="600">
              {text}
            </Text>
            <img
              src="/j/images/external-link-black.svg"
              style={{ marginLeft: 6 }}
              width={12}
              height={12}
              alt="external link"
              color="black"
            />
          </Flex>
        </Box>
      </a>
    );
  }

  getStep2Questions({ data }) {
    let questions = [residentStatusQuestion];
    const residentStatusCode = data.resident_status_code;
    const alienCode = data.alien_code;
    switch (residentStatusCode) {
      case "citizen":
      case "noncitizen_national": {
        questions = [residentStatusQuestion];
        break;
      }
      case "lawful_permanent_resident": {
        questions = [residentStatusQuestion, lawfulNumberQuestion];
        break;
      }
      case "alien_authorized_to_work": {
        questions = [residentStatusQuestion, alienCodeQuestion];
        if (alienCode && alienCode === "1") {
          questions = [
            residentStatusQuestion,
            alienCodeQuestion,
            alienNumberQuestion,
          ];
        }
        if (alienCode && alienCode === "2") {
          questions = [
            residentStatusQuestion,
            alienCodeQuestion,
            admissionNumberQuestion,
          ];
        }
        if (alienCode && alienCode === "3") {
          questions = [
            residentStatusQuestion,
            alienCodeQuestion,
            foreignPassportQuestion,
            alienCountryQuestion,
          ];
        }
        break;
      }
      default:
    }
    return this.injectAnswersToQuestions({ questions, data });
  }

  injectAnswersToQuestions({ questions, data }, step = undefined) {
    const { config } = this.props;
    const { prefilledData = {} } = this.props;
    const validateCodes = stateOptions.map((s) => s.codeValue);
    if (
      prefilledData.state &&
      validateCodes.indexOf(prefilledData.state) === -1
    ) {
      delete prefilledData.state;
    }
    if (data.state && validateCodes.indexOf(data.state) === -1) {
      delete data.state;
    }
    if (!this.shouldPrepopulate(config) && step == "step1") {
      Object.keys(data).forEach((key) => (data[key] = ""));
      Object.keys(prefilledData).forEach((key) => (prefilledData[key] = ""));
    }

    const newQuestions = questions.map((question) => {
      const value = data[question.id] || prefilledData[question.id] || "";
      // if (!data[question.id] && prefilledData[question.id]) {
      //   question.locked = true;
      // }
      const answer = {
        answer: {
          [question.id]: value,
        },
      };
      return {
        ...question,
        answer,
      };
    });
    return newQuestions;
  }

  getFullNameFromQuestions() {
    const { step1Questions } = this.state;
    const firstNameQuestion = step1Questions.find((q) => q.id === 'first_name');
    const middleInitialQuestion = step1Questions.find((q) => q.id === 'middle_initial');
    const lastNameQuestion = step1Questions.find((q) => q.id === 'last_name');
    const fullName = middleInitialQuestion.answer.answer.middle_initial ? `${firstNameQuestion.answer.answer.first_name} ${middleInitialQuestion.answer.answer.middle_initial} ${lastNameQuestion.answer.answer.last_name}` : `${firstNameQuestion.answer.answer.first_name} ${lastNameQuestion.answer.answer.last_name}`;
    return fullName;
  }

  getEmailFromQuestions() {
    const { step1Questions } = this.state;
    const emailQuestion = step1Questions.find((q) => q.id === 'email_address');
    return emailQuestion.answer.answer.email_address;
  }

  initHelloSign() {
    const { signLink } = this.state;
    const containerId = "iframe-container";

    this.hellosignClient = new HelloSign({
      clientId: helloSignClientId,
    });

    this.hellosignClient.once(HelloSign.events.SIGN, (eventData) => {
      const { event } = eventData;
      if (event === HelloSign.EVENT_SIGNED) {
        // Call api to mark this document as signed
        const { digest_key: digestKey } = this.props.action.i9_form_submission;
        const path = `${APIHOST}/v1/i9_form_submissions/:digest_key/signed`.replace(
          ':digest_key',
          digestKey
        );
        Axios.post(path, { sign_url: signLink })
          .then(() => {
            this.props.onSignedSuccess();
          })
          .catch((error) => {
            if (isNoticeGateError(error)) {
              this.routeToNoticeStep({ fromSigning: true });
              return;
            }
            let signError = 'Something wrong with the signing.';
            if (error && error.response && error.response.data) {
              const { error_code: errorCode, message } = error.response.data;
              if (errorCode === 60033) {
                signError = message;
              }
            }
            this.setState({ signError });
          });
      }
    });

    const options = {
      skipDomainVerification: skipDomainVerificationHelloSign,
      container: document.getElementById(containerId),
    };

    this.hellosignClient.open(signLink, options);
  }

  componentWillUnmount() {
    this.clearNoticeRefreshTimer();
    this.hellosignClient && this.hellosignClient.close();
  }

  setPendingSSN(pending) {
    this.setState((prevState) => ({
      pendingSSN: pending,
      showPendingSSNConfirm: false,
      step1Questions: prevState.step1Questions.map((q) => {
        if (q.id !== 'social_security_number') return q;
        return {
          ...q,
          is_required: !pending,
          hideInput: pending,
          answer: {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              social_security_number: pending ? '' : q.answer.answer.social_security_number,
            },
          },
        };
      }),
    }));
  }

  renderStep1Form(isReviewStep) {
    const { step1Questions: questions, pendingSSN, showPendingSSNConfirm } = this.state;

    const ssnQuestion = questions.find((q) => q.id === 'social_security_number');
    const ssnValue = (ssnQuestion && ssnQuestion.answer && ssnQuestion.answer.answer
      && ssnQuestion.answer.answer.social_security_number) || '';

    // The pending option is a link (only while the SSN field is empty) that
    // opens a confirmation popup, instead of an always-visible checkbox:
    // workers with an SSN kept checking the box by mistake, which cleared
    // their typed SSN and required a Section 1 correction.
    let pendingSSNComponent = null;
    if (this.isPendingSSNEnabled) {
      if (pendingSSN) {
        pendingSSNComponent = (
          <S.QuestionWrapper mb="24px" mt="-12px">
            <Box p="14px" bg="#fafafa" border="1px solid #e3e4e6" borderRadius="8px">
              <Text fontSize="14px" fontWeight="600">SSN marked as pending</Text>
              <Text fontSize="12px" color="#787883" mt="4px">
                Your employer will be notified when you submit this step. Be sure
                to provide your SSN to your employer as soon as you receive it.
              </Text>
              {!isReviewStep && (
                <Box mt="10px">
                  <SecondaryButton
                    type="button"
                    onClick={() => this.setPendingSSN(false)}
                  >
                    Enter SSN instead
                  </SecondaryButton>
                </Box>
              )}
            </Box>
          </S.QuestionWrapper>
        );
      } else if (showPendingSSNConfirm && !isReviewStep) {
        const closeConfirm = () => this.setState({ showPendingSSNConfirm: false });
        pendingSSNComponent = (
          <ModalProvider backgroundComponent={PendingSSNModalBackground}>
            <PendingSSNModal
              isOpen
              onBackgroundClick={closeConfirm}
              onEscapeKeydown={closeConfirm}
            >
              <Flex column width={1}>
                <Flex justifyContent="space-between" alignItems="flex-start">
                  <Text fontSize="16px" fontWeight="600">
                    Mark your SSN as pending?
                  </Text>
                  <CloseOutlined
                    style={{ color: '#464D5D', cursor: 'pointer', fontSize: '16px' }}
                    onClick={closeConfirm}
                  />
                </Flex>
                <Box mt="10px" fontSize="13px" color="#666666">
                  <ul style={{ margin: 0, paddingLeft: '18px' }}>
                    <li style={{ marginBottom: '8px' }}>
                      Only choose this if you applied with the Social Security
                      Administration and your card has not arrived.
                    </li>
                    <li>
                      Your employer will be notified when you submit this step,
                      and parts of onboarding and payroll may be delayed.
                    </li>
                  </ul>
                </Box>
                <Box mt="16px">
                  <Button
                    type="button"
                    style={{ display: 'block', width: '100%' }}
                    onClick={() => this.setPendingSSN(true)}
                  >
                    Mark as pending
                  </Button>
                  <button
                    type="button"
                    onClick={closeConfirm}
                    style={{
                      display: 'block',
                      width: '100%',
                      marginTop: '8px',
                      padding: '9px 0',
                      border: 0,
                      borderRadius: '8px',
                      background: 'none',
                      color: '#666666',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Enter SSN
                  </button>
                </Box>
              </Flex>
            </PendingSSNModal>
          </ModalProvider>
        );
      } else if (!isReviewStep && !ssnValue) {
        pendingSSNComponent = (
          // Negative top margin pulls the link up against the SSN input it
          // belongs to (the question wrapper above carries a 20px bottom margin)
          <S.QuestionWrapper mb="24px" mt="-14px">
            <Text
              fontSize="13px"
              color="#666666"
              style={{
                cursor: 'pointer',
                textDecoration: 'underline dotted',
                textUnderlineOffset: '3px',
                display: 'inline-block',
              }}
              onClick={() => this.setState({ showPendingSSNConfirm: true })}
            >
              Applied for an SSN but haven&apos;t received it yet?
            </Text>
          </S.QuestionWrapper>
        );
      }
    }

    return (
      <FormConsumer
        questions={questions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = this.state.step1Questions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.anser,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          this.setState({ step1Questions: questions });
        }}
        ref={this.step1Form}
        {...(this.isPendingSSNEnabled && pendingSSNComponent && {
          appendCustomComponent: {
            social_security_number: pendingSSNComponent,
          }
        })}
      />
    );
  }

  saveFormData(form, data, successCallback) {
    const { digest_key: digestKey } = this.props.action.i9_form_submission;
    this.setState({ isSubmitting: true, saveDataError: false });
    const path = `${APIHOST}/v1/i9_form_submissions/${digestKey}`;

    Axios.put(path, toFormData({ data }))
      .then((responseData) => {
        this.setState({ isSubmitting: false });
        successCallback(responseData);
      })
      .catch((error) => {
        if (isNoticeGateError(error)) {
          this.routeToNoticeStep();
          return;
        }
        let saveDataError = "Something went wrong while saving the form";
        if (error && error.response && error.response.data) {
          const { error_code: errorCode, message } = error.response.data;
          if (errorCode === 60032) {
            saveDataError = message;
          } else if (errorCode === 60040 && error.response.data.details) {
            form.updateServerValidations(error.response.data.details);
          }
        }
        this.setState({ isSubmitting: false, saveDataError });
      });
  }

  renderStep1Button() {
    const { step1Questions: questions, isSubmitting } = this.state;
    const { disabled, disabledText } = this.workerStepLock();

    return (
      <Flex flexDirection="column">
        <Button
          disabled={disabled}
          onClick={() => {
            if (this.step1Form && this.step1Form.current) {
              this.step1Form.current.validate((error) => {
                if (error) return;
                const data = questions.reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );

                // Add pending_ssn checkbox value only if feature flag is enabled
                if (this.isPendingSSNEnabled) {
                  data.pending_ssn = this.state.pendingSSN;
                }

                this.saveFormData(this.step1Form.current, data, () => {
                  try {
                    const { uuid } = this.props.i9Form;
                    const logPath = `${APIHOST}/v1/i9/employee/i9_forms/${uuid}/i9_form_audit_logs`;
                    const isCorrection = this.props.action.i9_form_submission && this.props.action.i9_form_submission.correction;
                    Axios.post(logPath, {
                      event_type: isCorrection ? 'section1_update_initiated' : 'section1_initiated',
                      source: 'onboarding',
                      audited_at: this.state.section1InitialTime,
                      operator_name: this.getFullNameFromQuestions(),
                      operator_email: this.getEmailFromQuestions(),
                    });
                  } finally {
                    this.setState({ currentStep: STEPS.step2 });
                  }
                });
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </Button>
        <Flex justifyContent="flex-end">
          {disabled && <ActionNote text={disabledText} />}
        </Flex>
      </Flex>
    );
  }

  renderI9Link() {
    return (
      <div>
        <Text fontSize="14px">
          This following steps are required to prove your employment eligiblity.
        </Text>
        <a
          target="_blank"
          rel="noopener noreferrer"
          style={{
            lineHeight: "20px",
            textDecoration: "none",
            color: "#3F51B5",
          }}
          href="https://www.uscis.gov/sites/default/files/document/forms/i-9instr.pdf"
        >
          <Flex alignItems="center">
            <Text>Learn more about Form I-9 </Text>
            <img
              src="/j/images/external-link.svg"
              style={{ marginLeft: 6 }}
              width={16}
              height={16}
              alt="external link"
            />
          </Flex>
        </a>
      </div>
    );
  }

  renderStep1() {
    return (
      <Flex column>
        {this.renderFixedModuleHeader(
          "Instructions for Form I-9",
          "https://www.uscis.gov/sites/default/files/document/forms/i-9instr.pdf"
        )}

        <Text fontSize="22px" bold>
          Personal Information
        </Text>
        <Text fontSize="14px">
          This following steps are required to prove your employment eligiblity.
        </Text>
        {/* {this.renderI9Link()} */}
        <Flex column mt={44}>
          {this.renderStep1Form()}
        </Flex>
        <Box mt={30} />
        {this.renderStep1Button()}
      </Flex>
    );
  }

  renderStep2Form(isReviewStep) {
    const {
      step2Questions: questions,
      data,
      expirationDate,
      noExpirationDate,
      expirationDateError,
    } = this.state;

    const expirationDateComponent = data.resident_status_code ===
      "alien_authorized_to_work" && (
      <S.QuestionWrapper mb="24px">
        <Flex>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            width={1}
            mb="7px"
          >
            <S.QuestionLabel>
              Work authorization expiration date
            </S.QuestionLabel>
          </Flex>
        </Flex>
        <Flex position="relative" column>
          <Flex>
            <PikadayReact
              value={expirationDate}
              width="auto"
              disabled={isReviewStep || noExpirationDate}
              dateFormat="MM/DD/YYYY"
              pikadayConfig={{
                yearRange: [dayjs().year(), dayjs().year() + 100],
                disableDayFn: (date) => {
                  if (!date) return false;
                  const today = new Date();
                  return dayjs(date).isSameOrBefore(dayjs(today));
                },
              }}
              onChange={(value) => {
                this.setState({ expirationDate: value });
              }}
            />
          </Flex>
        </Flex>
        <label>
          <Flex alignItems="flex-top">
            <S.Input
              disabled={isReviewStep}
              type="checkbox"
              onChange={(e) => {
                this.setState({
                  noExpirationDate: e.target.checked,
                  expirationDateError: undefined,
                  expirationDate: "",
                });
              }}
              checked={noExpirationDate}
            />
            <Box ml="6px" />
            <Box mt="4px">
              My work authorization does not have an expiration date
            </Box>
          </Flex>
        </label>
        {expirationDateError && <Text color="red">{expirationDateError}</Text>}
      </S.QuestionWrapper>
    );
    return (
      <Flex column>
        <FormConsumer
          questions={questions}
          {...(isReviewStep && {
            viewResultAsManager: true,
            reviewActions: [],
          })}
          onQuestionChange={({ question, value }) => {
            let newData = {
              ...data,
              [question.id]: value,
            };
            let { expirationDate } = this.state;
            if (question.id === "resident_status_code") {
              expirationDate = "";
              const cleanupArray = [
                "alien_code",
                "lawful_permanent_resident_alien_registration_or_uscis_number",
                "alien_registration_or_uscis_number",
                "admission_number",
                "passport_number",
                "country_of_issuance",
              ];
              newData = _.omit(newData, cleanupArray);
            } else if (question.id === "alien_code") {
              const cleanupArray = [
                "alien_registration_or_uscis_number",
                "admission_number",
                "passport_number",
                "country_of_issuance",
              ];
              newData = _.omit(newData, cleanupArray);
            }
            this.setState({
              expirationDate,
              data: newData,
              step2Questions: this.getStep2Questions({ data: newData }),
            });
          }}
          ref={this.step2Form}
          appendCustomComponent={{
            resident_status_code: expirationDateComponent,
          }}
        />
      </Flex>
    );
  }

  renderStep2() {
    const {
      noExpirationDate,
      expirationDate,
      step2Questions: questions,
      data,
    } = this.state;
    return (
      <Flex column>
        {this.renderFixedModuleHeader(
          "Instructions for Form I-9",
          "https://www.uscis.gov/sites/default/files/document/forms/i-9instr.pdf"
        )}

        <Text fontSize="22px" bold>
          Citizenship or immigration status
        </Text>
        {/* {this.renderI9Link()} */}
        <Flex column mt={44}>
          {this.renderStep2Form()}
        </Flex>
        <Flex mt={30}>
          <SecondaryButton
            onClick={() => {
              this.setState({ currentStep: STEPS.step1, saveDataError: false });
            }}
          >
            Back
          </SecondaryButton>
          <Flex flex={1} column ml={2}>
            <Button
              onClick={() => {
                if (this.step2Form && this.step2Form.current) {
                  this.step2Form.current.validate((error) => {
                    this.setState({ expirationDateError: undefined });
                    const expirationDateError = (() => {
                      if (data.resident_status_code !== "4") return;
                      if (!data.alien_code) return;
                      if (noExpirationDate) return;
                      if (expirationDate) return;
                      const errorMessage = "Please input expiration date";
                      this.setState({ expirationDateError: errorMessage });
                      return errorMessage;
                    })();

                    if (error || expirationDateError) {
                      return;
                    }
                    // Because the API is for incrementally saving data, and some data
                    // are mutually exclusive, so we need have a base empty value for
                    // those fields
                    const emptyData = {
                      lawful_permanent_resident_alien_registration_or_uscis_number:
                        "",
                      alien_registration_or_uscis_number: "",
                      admission_number: "",
                      passport_number: "",
                      country_of_issuance: "",
                      alien_authorized_to_work_expiration_date: "",
                    };
                    const step2Data = questions.reduce(
                      (acc, question) => ({
                        ...acc,
                        ...question.answer.answer,
                      }),
                      {}
                    );
                    if (expirationDate) {
                      step2Data.alien_authorized_to_work_expiration_date =
                        expirationDate;
                    }
                    this.saveFormData(
                      this.step2Form.current,
                      { ...emptyData, ...step2Data },
                      () => {
                        this.setState({ currentStep: STEPS.step3 });
                      }
                    );
                  });
                }
              }}
              loading={this.state.isSubmitting}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  renderStep3() {
    const { viewResultAsManager } = this.props;
    let pdfURL;
    if (viewResultAsManager) {
      const { digest_key: digestKey } = this.props.action.i9_form_submission;
      pdfURL = getSecurePDFLink(digestKey);
    }
    return (
      <Flex column>
        {!viewResultAsManager &&
          this.renderFixedModuleHeader(
            "Instructions for Form I-9",
            "https://www.uscis.gov/sites/default/files/document/forms/i-9instr.pdf"
          )}
        <Text fontSize="22px" bold>
          Review
        </Text>
        <Flex column mt={33}>
          <Text fontSize="18px" bold>
            Personal information
          </Text>
          {/* {this.renderI9Link()} */}
          <Box mt={16} />
          {this.renderStep1Form(true)}

          {/* step 2 result */}
          <Flex mt={44} column>
            <Text fontSize="18px" bold>
              Resident Status
            </Text>
            <Box mt={16} />
            {this.renderStep2Form(true)}
          </Flex>
        </Flex>
        {this.props.viewResultAsManager &&
          this.props.companyStaff.pii_permission === "full_access" && (
            <Flex column>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={pdfURL} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
          )}
        <AttestationCheckbox
          id="cfaComplianceCheck"
          name="cfaComplianceCheckbox"
          checked={this.state.cfaComplianceCheck}
          error={this.state.cfaComplianceCheckError}
          onChange={(checked) => {
            if (checked) {
              this.setState({ cfaComplianceCheckError: false });
            }
            this.setState({ cfaComplianceCheck: checked });
          }}
        >
          I am aware that federal law provides for imprisonment and/or
          fines for false statements, or the use of false documents, in
          connection with the completion of this form. I attest, under penalty
          of perjury, that this information, including my selection of the box
          attesting to my citizenship or immigration status, is true and correct.
        </AttestationCheckbox>

        <Flex mt={50}>
          <SecondaryButton
            disabled={this.props.viewResultAsManager}
            onClick={() => {
              this.setState({ currentStep: STEPS.step2, saveDataError: false });
            }}
          >
            Back
          </SecondaryButton>
          <Flex flex={1} column ml={2}>
            <Button
              disabled={this.props.viewResultAsManager}
              onClick={() => {
                if (!this.state.cfaComplianceCheck) {
                  this.setState({ cfaComplianceCheckError: true });
                  return;
                }
                const { digest_key: digestKey } =
                  this.props.action.i9_form_submission;
                const path = `${APIHOST}/v1/i9_form_submissions/${digestKey}/sign`;
                this.setState({ isSubmitting: true, saveDataError: false });
                Axios.post(path)
                  .then((responseData) => {
                    const { sign_url: signLink } = responseData.data;
                    this.setState(
                      {
                        isSubmitting: false,
                        signLink,
                        currentStep: STEPS.step4,
                      },
                      () => {
                        this.initHelloSign();
                      }
                    );
                  })
                  .catch((error) => {
                    if (isNoticeGateError(error)) {
                      this.routeToNoticeStep();
                      return;
                    }
                    this.setState({
                      isSubmitting: false,
                      saveDataError: "Something went wrong while preparing the form for signing",
                    });
                  });
              }}
              loading={this.state.isSubmitting}
            >
              Finalize and Sign
            </Button>
          </Flex>
        </Flex>
        <Flex justifyContent="flex-end">
          {this.props.viewResultAsManager && (
            <ActionNote text="This step has already been completed." />
          )}
        </Flex>
      </Flex>
    );
  }

  renderStep4() {
    const { signError } = this.state;
    return (
      <Flex column>
        {this.renderFixedModuleHeader(
          "Instructions for Form I-9",
          "https://www.uscis.gov/sites/default/files/document/forms/i-9instr.pdf"
        )}

        <Text fontSize="22px" bold>
          Sign Form I-9
        </Text>
        {/* {this.renderI9Link()} */}
        <Flex column mt={44} mx={this.isOnboardingV2 ? 0 : '-100px'}>
          <div id="iframe-container" style={{ height: "640px" }} />
        </Flex>
        {signError && <Text color="red">{signError}</Text>}
      </Flex>
    );
  }

  renderNoticeStep() {
    const [notice] = this.currentPendingNotices();
    const { digest_key: digestKey } = this.props.action.i9_form_submission;
    const { disabled, disabledText } = this.workerStepLock();
    return (
      <Flex column>
        {this.renderFixedModuleHeader(
          "Instructions for Form I-9",
          "https://www.uscis.gov/sites/default/files/document/forms/i-9instr.pdf"
        )}
        {notice ? (
          <NoticeStep
            key={notice.key}
            notice={notice}
            digestKey={digestKey}
            disabled={disabled}
            disabledText={disabledText}
            onAcknowledged={(item) => this.handleNoticeAcknowledged(item)}
          />
        ) : (
          <Text fontSize="14px" color="#787883">Loading the notice you need to acknowledge...</Text>
        )}
      </Flex>
    );
  }

  render() {
    const { currentStep, saveDataError } = this.state;
    const moduleName = this.props.action.module_name;
    const showNotice = this.showNoticeStep();
    return (
      <Flex column>
        {
          this.isOnboardingV2 && <PageTitle title={moduleName} />
        }
        <Flex column style={this.isOnboardingV2 ? { paddingLeft: '4px', paddingRight: '4px' } : { width: "310px", margin: "auto" }}>
          {showNotice && this.renderNoticeStep()}
          {!showNotice && currentStep === STEPS.step1 && this.renderStep1()}
          {!showNotice && currentStep === STEPS.step2 && this.renderStep2()}
          {!showNotice && currentStep === STEPS.step3 && this.renderStep3()}
          {saveDataError && (
            <Flex justifyContent="flex-end" mt={1}>
              <Text color="red">{saveDataError}</Text>
            </Flex>
          )}
        </Flex>
        <Flex column style={this.isOnboardingV2 ? { width: "100%", maxWidth: 900 } : { width: "50%", margin: "auto", maxWidth: 800 }}>
          {!showNotice && currentStep === STEPS.step4 && this.renderStep4()}
        </Flex>
      </Flex>
    );
  }
}

export default EmployeeFillForm;
